/**
 * ============================================================
 * LAB CONTEXT — Provider de Descoberta Contextual de Roteiros
 * ============================================================
 * Monitora a rota ativa e diálogos abertos, mantendo em tempo
 * real a lista de roteiros disponíveis para o contexto atual.
 *
 * Também expõe window.__AC_LAB_RUNTIME__ para consumo externo
 * por Playwright, agentes de IA e DevTools.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFlowForDialog, getFlowsForRoute } from './lab-route-map';
import { getUnlockedFlows, getPipelineOrder, LAB_DAG } from './lab-dag';

// ─── Tipos ────────────────────────────────────────────────────

/**
 * Regra de pré-validação declarativa para passos de navegação.
 * O executor verifica esses campos ANTES de executar o clique.
 * Se algum campo falhar a validação, o passo para e informa o campo faltante.
 */
export interface LabPreValidacao {
  /** Seletor CSS do campo a verificar (ex: 'input[name="numero"]') */
  selector: string;
  /** Nome amigável para exibir na mensagem de erro */
  label: string;
  /** Tipo de validação */
  tipo: 'required' | 'minlength' | 'pattern';
  /** Tamanho mínimo (para tipo 'minlength') */
  min?: number;
  /** Expressão regular como string (para tipo 'pattern') */
  pattern?: string;
}

export interface LabStep {
  ordem: number;
  acao: string;
  fala: string;
  elementoLogico?: string | null;
  texto?: string;
  urlContem?: string;
  /**
   * Pré-validações executadas ANTES da ação deste passo.
   * Apenas relevante para ações 'mover_e_clicar' e 'clicar'.
   * Se qualquer validação falhar, o passo retorna blockingAlert=true
   * e o autoMode é pausado até intervenção do usuário.
   */
  preValidar?: LabPreValidacao[];
}

/**
 * Handler de dialog interruptor declarado no manifest.
 * O executor detecta pelo selector e executa o handler automaticamente
 * sem interromper o flow principal.
 */
export interface LabContexto {
  /** Seletor CSS do elemento de dialog (ex: '[data-lab-dialog-type="confirmation"]') */
  selector: string;
  /** Texto parcial esperado no título/conteúdo do dialog (opcional) */
  tituloParcial?: string;
  /** Ação a executar: 'clicar_acao_segura' | 'aguardar' | 'fechar' */
  acao: 'clicar_acao_segura' | 'aguardar' | 'fechar';
  /** Fala do narrador quando o dialog é detectado */
  fala: string;
}

export interface LabFlowInfo {
  id: string;
  titulo: string;
  descricao: string;
  passos: number;
  dificuldade: string;
  duracao: string;
  /** URL de partida do roteiro (ex: https://localhost:3443/master/escolas) */
  urlInicial?: string;
}

export interface LabContextValue {
  /** Rota atual do React Router */
  currentRoute: string;

  /** ID do diálogo/modal aberto (null = nenhum) */
  openDialogId: string | null;

  /** Roteiros disponíveis para rota + dialog atual */
  availableFlows: LabFlowInfo[];

  /** Roteiro sendo executado no momento */
  activeFlowId: string | null;

  /** Informações do flow ativo (titulo, duracao etc.) */
  activeFlowInfo: LabFlowInfo | null;

  /** Passos completos do flow ativo (com fala, acao, elementoLogico) */
  activeSteps: LabStep[];

  /** Índice do passo atual (0-based) */
  activeStepIndex: number;

  /** true durante execução de roteiro */
  isExecuting: boolean;

  /** Registrar abertura de diálogo para descoberta contextual */
  registerDialog: (dialogId: string) => void;

  /** Registrar fechamento de diálogo */
  unregisterDialog: (dialogId: string) => void;

  /** Iniciar execução de um roteiro */
  startFlow: (flowId: string, startStep?: number) => void;

  /** Avançar para o próximo passo */
  nextStep: () => void;

  /** Abortar execução do roteiro ativo */
  abortFlow: () => void;

  /** Roteiros desbloqueados após o último fluxo concluído */
  unlockedFlows: string[];

  /** Contextos (handlers de dialogs interruptores) do flow ativo */
  activeContextos: LabContexto[];

  /** Modo de reprodução de trilha contínua ativado */
  isContinuousMode: boolean;

  /** Alterna o modo contínuo */
  toggleContinuousMode: () => void;

  /** Define o modo contínuo */
  setContinuousMode: (val: boolean) => void;

  /** Último fluxo concluído com sucesso */
  lastCompletedFlowId: string | null;

  /** Estado de transição para o próximo fluxo */
  flowTransition: { nextFlow: LabFlowInfo; countdown: number; previousTitle: string } | null;

  /** Cancelar transição para o próximo fluxo */
  cancelTransition: () => void;

  /** Iniciar imediatamente o próximo fluxo da trilha */
  startNextFlowNow: () => void;
}

// ─── Contexto ─────────────────────────────────────────────────

const LabContext = createContext<LabContextValue | null>(null);

// ─── Hook público ─────────────────────────────────────────────

export function useLabContext(): LabContextValue {
  const ctx = useContext(LabContext);
  if (!ctx) {
    throw new Error('useLabContext deve ser usado dentro de <LabContextProvider>');
  }
  return ctx;
}

/**
 * Versão null-safe do useLabContext.
 * Retorna null quando usado fora do LabContextProvider (ex: modais em páginas públicas).
 * Use este hook em componentes de UI de baixo nível (como Modal.tsx) que podem ser
 * renderizados fora da árvore do LabContextProvider.
 */
export function useLabContextSafe(): LabContextValue | null {
  return useContext(LabContext);
}

// ─── Provider ─────────────────────────────────────────────────

interface ManifestFlowShape {
  id: string;
  titulo: string;
  descricao: string;
  duracao: string;
  dificuldade: string;
  urlInicial?: string;
  passos: Array<LabStep>;
  /** Handlers de dialogs interruptores declarados no CANONICAL_FLOWS do gerador */
  contextos?: Array<LabContexto>;
}

interface ManifestShape {
  categories: Array<{
    id: string;
    flows: Array<ManifestFlowShape>;
  }>;
}

const EMPTY_MANIFEST: ManifestShape = { categories: [] };

interface LabContextProviderProps {
  children: React.ReactNode;
  /** Manifesto estático (opcional). Se null, carrega via fetch('/lab_manifest.json'). */
  manifest?: ManifestShape | null;
  /** Ativar apenas em ambientes de desenvolvimento/lab */
  enabled?: boolean;
}

export function LabContextProvider({
  children,
  manifest: propManifest = null,
  enabled = true,
}: LabContextProviderProps) {
  const location = useLocation();
  const [loadedManifest, setLoadedManifest] = useState<ManifestShape>(propManifest ?? EMPTY_MANIFEST);
  const [openDialogId, setOpenDialogId] = useState<string | null>(null);
  const [activeFlowId, setActiveFlowId] = useState<string | null>(null);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [isExecuting, setIsExecuting] = useState(false);
  const [unlockedFlows, setUnlockedFlows] = useState<string[]>([]);
  const dialogStackRef = useRef<string[]>([]);

  // Carregar manifesto via fetch se não foi passado como prop
  useEffect(() => {
    if (propManifest || !enabled) return;
    fetch('/lab_manifest.json')
      .then(r => r.json())
      .then((data: ManifestShape) => setLoadedManifest(data))
      .catch(e => console.warn('[LabContext] Falha ao carregar lab_manifest.json:', e));
  }, [propManifest, enabled]);

  const manifest = loadedManifest;

  // Índice de metadados do flow (sem passos detalhados)
  const flowIndex = useMemo(() => {
    const index: Record<string, LabFlowInfo> = {};
    for (const cat of manifest.categories) {
      for (const flow of cat.flows) {
        index[flow.id] = {
          id: flow.id,
          titulo: flow.titulo,
          descricao: flow.descricao,
          passos: flow.passos.length,
          dificuldade: flow.dificuldade,
          duracao: flow.duracao,
          urlInicial: flow.urlInicial,
        };
      }
    }
    return index;
  }, [manifest]);

  // Índice completo com passos detalhados (para o overlay de execução)
  const flowStepsIndex = useMemo(() => {
    const index: Record<string, LabStep[]> = {};
    for (const cat of manifest.categories) {
      for (const flow of cat.flows) {
        index[flow.id] = flow.passos;
      }
    }
    return index;
  }, [manifest]);

  const activeSteps = useMemo<LabStep[]>(() => {
    if (!activeFlowId) return [];
    return flowStepsIndex[activeFlowId] ?? [];
  }, [activeFlowId, flowStepsIndex]);

  // Índice de contextos (handlers de dialogs) por flow id
  const flowContextosIndex = useMemo(() => {
    const index: Record<string, LabContexto[]> = {};
    for (const cat of manifest.categories) {
      for (const flow of cat.flows) {
        index[flow.id] = flow.contextos ?? [];
      }
    }
    return index;
  }, [manifest]);

  const activeContextos = useMemo<LabContexto[]>(() => {
    if (!activeFlowId) return [];
    return flowContextosIndex[activeFlowId] ?? [];
  }, [activeFlowId, flowContextosIndex]);

  const activeFlowInfo = useMemo<LabFlowInfo | null>(() => {
    if (!activeFlowId) return null;
    return flowIndex[activeFlowId] ?? null;
  }, [activeFlowId, flowIndex]);

  // Calcular roteiros disponíveis para rota + dialog atual
  const availableFlows = useMemo<LabFlowInfo[]>(() => {
    if (!enabled) return [];

    let flowIds: string[] = [];

    if (openDialogId) {
      // Se há diálogo aberto, priorizar o fluxo específico do diálogo
      const dialogMapping = getFlowForDialog(openDialogId);
      if (dialogMapping) {
        flowIds = [dialogMapping.flowId];
      }
    }

    // Sempre incluir fluxos da rota atual
    const routeFlows = getFlowsForRoute(location.pathname);
    for (const id of routeFlows) {
      if (!flowIds.includes(id)) {
        flowIds.push(id);
      }
    }

    return flowIds
      .map(id => flowIndex[id])
      .filter((f): f is LabFlowInfo => f !== undefined);
  }, [enabled, location.pathname, openDialogId, flowIndex]);

  const [isContinuousMode, setIsContinuousMode] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('ac_lab_continuous_mode');
      return saved !== null ? saved === 'true' : true; // Padrão: ligado
    } catch {
      return true;
    }
  });

  const [lastCompletedFlowId, setLastCompletedFlowId] = useState<string | null>(null);
  const [flowTransition, setFlowTransition] = useState<{ nextFlow: LabFlowInfo; countdown: number; previousTitle: string } | null>(null);

  const toggleContinuousMode = useCallback(() => {
    setIsContinuousMode(prev => {
      const next = !prev;
      try {
        localStorage.setItem('ac_lab_continuous_mode', String(next));
      } catch {}
      return next;
    });
  }, []);

  const setContinuousMode = useCallback((val: boolean) => {
    setIsContinuousMode(val);
    try {
      localStorage.setItem('ac_lab_continuous_mode', String(val));
    } catch {}
  }, []);

  // Registrar/desregistrar diálogos
  const registerDialog = useCallback((dialogId: string) => {
    dialogStackRef.current = [...dialogStackRef.current, dialogId];
    setOpenDialogId(dialogId);
  }, []);

  const unregisterDialog = useCallback((dialogId: string) => {
    dialogStackRef.current = dialogStackRef.current.filter(id => id !== dialogId);
    const top = dialogStackRef.current[dialogStackRef.current.length - 1] ?? null;
    setOpenDialogId(top);
  }, []);

  // Iniciar execução de roteiro
  const startFlow = useCallback((flowId: string, startStep = 0) => {
    setFlowTransition(null);
    setActiveFlowId(flowId);
    setActiveStepIndex(startStep);
    setIsExecuting(true);
  }, []);

  // Cancelar transição
  const cancelTransition = useCallback(() => {
    setFlowTransition(null);
  }, []);

  const navigate = useNavigate();

  // Iniciar próximo fluxo da trilha imediatamente
  const startNextFlowNow = useCallback(() => {
    if (!flowTransition) return;
    const { nextFlow } = flowTransition;
    setFlowTransition(null);
    if (nextFlow.urlInicial) {
      try {
        const targetPath = nextFlow.urlInicial.startsWith('http')
          ? new URL(nextFlow.urlInicial).pathname
          : nextFlow.urlInicial;
        if (location.pathname !== targetPath) {
          sessionStorage.setItem('ac_lab_pending_flow', nextFlow.id);
          navigate(targetPath);
          return;
        }
      } catch {}
    }
    startFlow(nextFlow.id, 0);
  }, [flowTransition, location.pathname, navigate, startFlow]);

  // Contagem regressiva da transição sequencial
  useEffect(() => {
    if (!flowTransition || flowTransition.countdown <= 0) return;
    const timer = setInterval(() => {
      setFlowTransition(prev => {
        if (!prev) return null;
        if (prev.countdown <= 1) {
          clearInterval(timer);
          setTimeout(() => {
            const next = prev.nextFlow;
            if (next.urlInicial) {
              try {
                const targetPath = next.urlInicial.startsWith('http')
                  ? new URL(next.urlInicial).pathname
                  : next.urlInicial;
                if (location.pathname !== targetPath) {
                  sessionStorage.setItem('ac_lab_pending_flow', next.id);
                  navigate(targetPath);
                  return;
                }
              } catch {}
            }
            startFlow(next.id, 0);
          }, 60);
          return null;
        }
        return { ...prev, countdown: prev.countdown - 1 };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [flowTransition, location.pathname, navigate, startFlow]);

  // Auto-iniciar fluxo contínuo vindo de redirecionamento (&lab-active=true ou sessionStorage)
  useEffect(() => {
    if (!enabled || loadedManifest.categories.length === 0) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const pendingFlow = params.get('lab-flow') || sessionStorage.getItem('ac_lab_pending_flow');
      if (pendingFlow && flowIndex[pendingFlow] && !isExecuting) {
        sessionStorage.removeItem('ac_lab_pending_flow');
        const timer = setTimeout(() => {
          startFlow(pendingFlow, 0);
        }, 300);
        return () => clearTimeout(timer);
      }
    } catch {
      // Ignora erro de acesso a window/storage
    }
  }, [enabled, loadedManifest, flowIndex, isExecuting, startFlow, location.pathname, location.search]);

  // Avançar passo
  const nextStep = useCallback(() => {
    if (!activeFlowId) return;
    const flow = flowIndex[activeFlowId];
    if (!flow) return;

    const nextIndex = activeStepIndex + 1;
    if (nextIndex >= flow.passos) {
      // Fluxo concluído!
      const completedId = activeFlowId;
      const currentTitle = flow.titulo;
      setIsExecuting(false);
      setActiveFlowId(null);
      setActiveStepIndex(0);
      setLastCompletedFlowId(completedId);
      
      const unlocked = getUnlockedFlows(completedId);
      setUnlockedFlows(unlocked);

      // Identifica próximo fluxo na cadeia do DAG
      const nextId = (unlocked && unlocked.length > 0) ? unlocked[0] : (LAB_DAG[completedId]?.unlocks?.[0] ?? null);
      const nextFlow = nextId ? flowIndex[nextId] : null;

      if (nextFlow) {
        setFlowTransition({
          nextFlow,
          countdown: isContinuousMode ? 3 : 0,
          previousTitle: currentTitle,
        });
      }
    } else {
      setActiveStepIndex(nextIndex);
    }
  }, [activeFlowId, activeStepIndex, flowIndex, isContinuousMode]);

  // Abortar fluxo ativo
  const abortFlow = useCallback(() => {
    setIsExecuting(false);
    setActiveFlowId(null);
    setActiveStepIndex(0);
    setFlowTransition(null);
  }, []);

  // Atualizar window.__AC_LAB_RUNTIME__ para consumo externo
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const runtime = {
      version: '3.1.0',
      currentRoute: location.pathname,
      openDialogId,
      availableFlows: availableFlows.map(f => ({
        id: f.id,
        titulo: f.titulo,
        passos: f.passos,
      })),
      activeFlowId,
      activeStepIndex,
      isExecuting,
      isContinuousMode,
      toggleContinuousMode,
      // API pública para Playwright e agentes de IA
      getFlowsForRoute: (route: string) =>
        getFlowsForRoute(route)
          .map(id => flowIndex[id])
          .filter(Boolean),
      getFlowsForDialog: (dialogId: string) => {
        const m = getFlowForDialog(dialogId);
        return m ? [{ id: m.flowId, startStep: m.startStep }] : [];
      },
      executeFlow: (flowId: string, startStep = 0) => {
        startFlow(flowId, startStep);
      },
      getPipelineOrder,
      getUnlockedFlows,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__AC_LAB_RUNTIME__ = runtime;
  }, [
    enabled,
    location.pathname,
    openDialogId,
    availableFlows,
    activeFlowId,
    activeStepIndex,
    isExecuting,
    isContinuousMode,
    toggleContinuousMode,
    flowIndex,
    startFlow,
  ]);

  const value: LabContextValue = {
    currentRoute: location.pathname,
    openDialogId,
    availableFlows,
    activeFlowId,
    activeFlowInfo,
    activeSteps,
    activeStepIndex,
    isExecuting,
    registerDialog,
    unregisterDialog,
    startFlow,
    nextStep,
    abortFlow,
    unlockedFlows,
    activeContextos,
    isContinuousMode,
    toggleContinuousMode,
    setContinuousMode,
    lastCompletedFlowId,
    flowTransition,
    cancelTransition,
    startNextFlowNow,
  };

  return <LabContext.Provider value={value}>{children}</LabContext.Provider>;
}

export default LabContext;
